import React, { useEffect, useMemo, useRef, useState } from "react";
import { translateApiMessage } from "../utils/formErrors";
import axiosInstance from "../utils/axiosInstance";
import Select from "../components/ui/Select";
import { UploadInputInterface } from "../components/ui/UploadInputInterface";
import {
  Wallet,
  Building2,
  FileText,
  Receipt,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

const ExpenseForm = () => {
  const [formData, setFormData] = useState({
    cottage: "",
    value: "",
    description: "",
    receipt: null,
  });

  const [cottages, setCottages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  // text user types into Select input
  const [cottageSearch, setCottageSearch] = useState("");

  // Prevent state updates from stale requests
  const reqSeq = useRef(0);

  const fetchCottages = async (searchTerm = "") => {
    setLoading(true);
    setError(null);
    const mySeq = ++reqSeq.current;

    try {
      const params = new URLSearchParams();
      if (searchTerm?.trim()) params.set("search", searchTerm.trim());
      // params.set("page_size", "50"); // if your endpoint supports it

      const url = `/cottages/${
        params.toString() ? `?${params.toString()}` : ""
      }`;
      const { data } = await axiosInstance.get(url);

      if (mySeq !== reqSeq.current) return; // ignore late response

      setCottages(data?.results || data || []);
    } catch (err) {
      if (mySeq !== reqSeq.current) return;
      setError("خطا در دریافت لیست کوتاژها");
      setCottages([]);
    } finally {
      if (mySeq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchCottages("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced refresh when user types in Select input
  useEffect(() => {
    const t = setTimeout(() => {
      fetchCottages(cottageSearch);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cottageSearch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((p) => ({ ...p, receipt: e.target.files?.[0] || null }));
  };

  const handleSelectChange = (selected) => {
    setFormData((p) => ({ ...p, cottage: selected ? selected.value : "" }));
  };

  const handleSelectInputChange = (inputValue, actionMeta) => {
    if (actionMeta.action === "input-change") {
      setCottageSearch(inputValue);
    }
    return inputValue;
  };

  const showToast = (type, msg) => {
    setToast({ open: true, type, msg });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(
      () => setToast({ open: false, type: "success", msg: "" }),
      2500
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError(null);

    try {
      const payload = new FormData();
      payload.append("cottage", formData.cottage);
      payload.append("value", formData.value);
      payload.append("description", formData.description || "");
      if (formData.receipt) payload.append("receipt", formData.receipt);

      await axiosInstance.post("/expenses/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setFormData({ cottage: "", value: "", description: "", receipt: null });
      setCottageSearch("");
      fetchCottages("");

      showToast("success", "هزینه با موفقیت ثبت شد.");
    } catch (err) {
      setError(translateApiMessage(err.response?.data?.detail) || "خطا در ثبت هزینه");
      showToast("error", "ثبت هزینه انجام نشد.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const cottageOptions = useMemo(
    () =>
      (cottages || []).map((c) => ({
        value: c.id,
        label: c.cottage_number || String(c.id),
      })),
    [cottages]
  );

  const selectedCottageOption = useMemo(
    () => cottageOptions.find((o) => o.value === formData.cottage) || null,
    [cottageOptions, formData.cottage]
  );

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        direction: "rtl",
        textAlign: "right",
        borderRadius: "1rem",
        padding: "0.25rem",
        minHeight: "48px",
        borderColor: state.isFocused ? "#93C5FD" : "#E2E8F0",
        boxShadow: state.isFocused
          ? "0 0 0 4px rgba(147,197,253,0.35)"
          : "none",
        ":hover": { borderColor: "#93C5FD" },
        backgroundColor: "rgba(255,255,255,0.98)",
      }),
      menu: (base) => ({
        ...base,
        textAlign: "right",
        zIndex: 80,
        borderRadius: "1rem",
        overflow: "hidden",
      }),
      option: (base, state) => ({
        ...base,
        textAlign: "right",
        backgroundColor: state.isSelected
          ? "#2563EB"
          : state.isFocused
          ? "#EFF6FF"
          : "#fff",
        color: state.isSelected ? "#fff" : "#0F172A",
      }),
      singleValue: (base) => ({
        ...base,
        textAlign: "right",
        color: "#0F172A",
      }),
      placeholder: (base) => ({
        ...base,
        textAlign: "right",
        color: "#64748B",
      }),
      input: (base) => ({ ...base, textAlign: "right", color: "#0F172A" }),
      indicatorSeparator: (base) => ({ ...base, display: "none" }),
      dropdownIndicator: (base) => ({ ...base, paddingLeft: "10px" }),
      clearIndicator: (base) => ({ ...base, paddingLeft: "10px" }),
    }),
    []
  );

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 font-vazir">
      {/* Top toast */}
      {toast.open && (
        <div className="fixed top-6 left-0 right-0 z-50 px-4">
          <div
            className={[
              "mx-auto max-w-lg rounded-2xl px-4 py-3 shadow-lg ring-1 backdrop-blur",
              toast.type === "success"
                ? "bg-emerald-50/90 ring-emerald-200 text-emerald-800"
                : "bg-red-50/90 ring-red-200 text-red-800",
            ].join(" ")}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {toast.type === "success" ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <AlertTriangle className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">{toast.msg}</span>
              </div>
              <button
                type="button"
                onClick={() =>
                  setToast({ open: false, type: "success", msg: "" })
                }
                className="text-sm opacity-70 hover:opacity-100"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Center card */}
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden">
          {/* Header */}
          <div className="p-6 sm:p-8 border-b border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div className="text-right">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
                  ثبت هزینه جدید
                </h1>
                <p className="mt-2 text-sm text-slate-600">
                  هزینه را ثبت کنید و در صورت نیاز رسید پرداخت را پیوست نمایید.
                </p>
              </div>
              <div className="shrink-0 rounded-2xl bg-blue-50 p-3 ring-1 ring-blue-100">
                <Wallet className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            <div className="grid grid-cols-1 gap-5">
              {/* Cottage */}
              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  انتخاب کوتاژ
                </label>

                <Select
                  name="cottage"
                  value={selectedCottageOption}
                  onChange={handleSelectChange}
                  onInputChange={handleSelectInputChange}
                  inputValue={cottageSearch}
                  options={cottageOptions}
                  isLoading={loading}
                  isClearable
                  placeholder={
                    loading ? "در حال بارگذاری..." : "جستجو و انتخاب کوتاژ"
                  }
                  noOptionsMessage={() =>
                    loading ? "در حال بارگذاری..." : "کوتاژ یافت نشد"
                  }
                  styles={selectStyles}
                  className="text-right"
                />
              </div>

              {/* Value */}
              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Wallet className="w-4 h-4 text-slate-500" />
                  مقدار (ریال)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="value"
                    value={formData.value}
                    onChange={handleChange}
                    placeholder="مثلاً ۵۰۰۰۰۰"
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:ring-4 focus:ring-blue-100"
                  />
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <FileText className="w-4 h-4 text-slate-500" />
                  شرح هزینه
                </label>
                <input
                  type="text"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="مثلاً خرید کابل شبکه"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:ring-4 focus:ring-blue-100"
                />
              </div>

              {/* Receipt */}
              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700">
                  <Receipt className="w-4 h-4 text-slate-500" />
                  رسید پرداخت
                </label>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <div dir="ltr">
                    <UploadInputInterface
                      name="receipt"
                      file={formData.receipt}
                      accept="image/*"
                      onFileChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-sm text-right">
                  {error}
                </div>
              )}

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={
                    submitLoading || !formData.cottage || !formData.value
                  }
                  className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 transition disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed"
                >
                  {submitLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      در حال ارسال...
                    </>
                  ) : (
                    "ثبت هزینه"
                  )}
                </button>

                <div className="mt-3 text-xs text-slate-500 text-right">
                  نکته: برای ثبت هزینه، انتخاب کوتاژ و وارد کردن مقدار الزامی
                  است.
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExpenseForm;
